// src/modules/units/tyres/shared/tyre-scope.js
//
// The single selected-yard scope for the whole tyre area.
//
// ONE STORE. Every view that offers a yard control — the header selector now, the
// detail screen's dropdown later — renders THIS value and writes back to it. None
// of them keeps a private copy to reconcile afterwards; that dual-source-of-truth
// is exactly the pattern this module keeps deleting (see effectiveStatus in
// tyre-status.js). Read with getSelectedYard(); change with setSelectedYard().
//
// Deliberately NOT on the legacy shared global (shared/utils/global.js). Yard
// scope is tyre-module state, carried on the tyre event bus, which is the
// strangler-fig line the module is drawn along — the next thing to leave the
// global rather than the next thing to join it.
//
// null === "All yards", and that is a real, valid, default state: nothing is
// hidden until a user opts into a yard. It is a VIEWING filter only — never a
// yard you can stamp on a record (you cannot create a tyre "in all yards"), so
// write paths must treat null as "no yard chosen", never as a wildcard.
//
// A change announces itself as SCOPE_CHANGED — its own event, not CHANGED.
// CHANGED means "a write happened" and is owned by tyres.api.js; a scope change
// is not a write, and conflating them would make every inspection or repair also
// reset the user's filters. So scope gets its own signal, emitted only from here,
// and views subscribe to it the same way they subscribe to CHANGED.

import { emitTyreEvent, TYRE_EVENTS } from './tyre-events.js';

// The yard object shape comes from GET /api/customer/{id}/yard: { Id, name,
// customerName, customerHierarchyOrder, ... } — note the capital-I `Id`. GET
// /api/tyre filters on `yardId`, so getSelectedYardId() is what a tyre query
// wants, and the Id -> yardId mapping lives at the query-build site, not on the
// yard object.
let selectedYard = null;

/** The selected yard object, or null for All yards. */
export function getSelectedYard() {
  return selectedYard;
}

/** The id to pass as ?yardId= to GET /api/tyre, or null for All yards. */
export function getSelectedYardId() {
  return selectedYard?.Id ?? null;
}

/** True when nothing is scoped — the whole customer is in view. */
export function isAllYards() {
  return selectedYard == null;
}

/**
 * Set the active yard (or null for All yards) and announce SCOPE_CHANGED.
 *
 * No-ops when the selection and its visible details are unchanged. A refreshed
 * object for the same id replaces stale details (for example, after renaming a
 * yard) and announces the change to views using its name or customer.
 */
export function setSelectedYard(yard) {
  const next = yard || null;
  const nextId = next?.Id ?? null;
  if (nextId === getSelectedYardId()) {
    if (!next) return;
    const detailsChanged = ['name', 'IMEI', 'customerId', 'customerName']
      .some((key) => next[key] !== selectedYard[key]);
    selectedYard = next;
    if (!detailsChanged) return;
  } else {
    selectedYard = next;
  }

  emitTyreEvent(TYRE_EVENTS.SCOPE_CHANGED, { yard: selectedYard });
}

/** Convenience for "show everything again". */
export function clearSelectedYard() {
  setSelectedYard(null);
}
